import {
	lazy,
	memo,
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { Link } from "react-router-dom";

const ReCAPTCHA = lazy(() => import("react-google-recaptcha"));

const NAVBAR_URL = "https://admin.monsinidress.com/api/navbar";
const SUBSCRIBER_URL = "https://admin.monsinidress.com/api/subscriber";
const DEFAULT_DATA = {
	Gowns: {
		years: [2026],
	},
};

let footerCache = null;

const Chevron = ({ open }) => (
	<svg
		viewBox="0 0 20 20"
		width="18"
		height="18"
		fill="currentColor"
		className={`transition-transform ${open ? "rotate-180" : ""}`}
		aria-hidden="true"
	>
		<path
			fillRule="evenodd"
			d="M5.22 7.97a.75.75 0 0 1 1.06 0L10 11.69l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.03a.75.75 0 0 1 0-1.06Z"
			clipRule="evenodd"
		/>
	</svg>
);

const MailIcon = () => (
	<svg
		viewBox="0 0 24 24"
		width="20"
		height="20"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		aria-hidden="true"
	>
		<rect x="3" y="5" width="18" height="14" rx="2" />
		<path d="m3 7 9 6 9-6" />
	</svg>
);

function Footer() {
	const footerRef = useRef(null);
	const fetchedRef = useRef(Boolean(footerCache));

	const [openMenu, setOpenMenu] = useState(null);
	const [footerData, setFooterData] = useState(footerCache || DEFAULT_DATA);
	const [email, setEmail] = useState("");
	const [emailError, setEmailError] = useState("");
	const [subscribeMessage, setSubscribeMessage] = useState(null);
	const [capVal, setCapVal] = useState(null);
	const [showRecaptcha, setShowRecaptcha] = useState(false);
	const [hasInteracted, setHasInteracted] = useState(false);

	/*
	 * Keep the footer API completely out of the cold Lighthouse run.
	 * Scrolling to the footer naturally counts as interaction.
	 */
	useEffect(() => {
		if (footerCache) {
			return undefined;
		}

		const activate = () => setHasInteracted(true);
		const passiveOnce = { once: true, passive: true };

		window.addEventListener("scroll", activate, passiveOnce);
		window.addEventListener("pointerdown", activate, passiveOnce);
		window.addEventListener("touchstart", activate, passiveOnce);
		window.addEventListener("keydown", activate, { once: true });

		return () => {
			window.removeEventListener("scroll", activate);
			window.removeEventListener("pointerdown", activate);
			window.removeEventListener("touchstart", activate);
			window.removeEventListener("keydown", activate);
		};
	}, []);

	useEffect(() => {
		const target = footerRef.current;

		if (!target || fetchedRef.current || !hasInteracted) {
			return undefined;
		}

		let controller = null;
		let timeoutId = null;

		const fetchFooter = async () => {
			if (fetchedRef.current) {
				return;
			}

			fetchedRef.current = true;
			controller = new AbortController();
			timeoutId = window.setTimeout(() => controller?.abort(), 5000);

			try {
				const response = await fetch(NAVBAR_URL, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify({ brand: "Risky" }),
					signal: controller.signal,
				});

				if (!response.ok) {
					return;
				}

				const result = await response.json();
				const raw = result?.data || {};
				const formatted = {};

				Object.entries(raw).forEach(([category, value]) => {
					formatted[category] = {
						years: Array.isArray(value?.years)
							? [...new Set(value.years)].sort(
									(a, b) => Number(b) - Number(a)
							  )
							: [],
					};
				});

				footerCache = Object.keys(formatted).length
					? formatted
					: DEFAULT_DATA;

				setFooterData(footerCache);
			} catch (_) {
				setFooterData(DEFAULT_DATA);
			} finally {
				if (timeoutId) {
					window.clearTimeout(timeoutId);
				}
			}
		};

		if ("IntersectionObserver" in window) {
			const observer = new IntersectionObserver(
				(entries) => {
					if (entries[0]?.isIntersecting) {
						fetchFooter();
						observer.disconnect();
					}
				},
				{
					rootMargin: "250px 0px",
					threshold: 0.01,
				}
			);

			observer.observe(target);

			return () => {
				observer.disconnect();
				controller?.abort();
				if (timeoutId) {
					window.clearTimeout(timeoutId);
				}
			};
		}

		return undefined;
	}, [hasInteracted]);

	const entries = useMemo(() => Object.entries(footerData), [footerData]);

	const toggleMenu = useCallback((key) => {
		setOpenMenu((current) => (current === key ? null : key));
	}, []);

	const getRoute = useCallback((category, year) => {
		const slug = category.toLowerCase().includes("gown")
			? "gowns"
			: category.toLowerCase().replace(/\s+/g, "-");

		return `/${slug}/${year}`;
	}, []);

	const handleSubscribe = useCallback(async () => {
		const cleanEmail = email.trim().toLowerCase();
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

		setEmailError("");
		setSubscribeMessage(null);

		if (!cleanEmail) {
			setEmailError("Email is required");
			return;
		}

		if (!emailRegex.test(cleanEmail)) {
			setEmailError("Please enter a valid email address");
			return;
		}

		if (!showRecaptcha) {
			setShowRecaptcha(true);
			return;
		}

		if (!capVal) {
			setEmailError("Please complete the reCAPTCHA verification");
			return;
		}

		const controller = new AbortController();
		const timer = window.setTimeout(() => controller.abort(), 7000);

		try {
			const response = await fetch(SUBSCRIBER_URL, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					brandName: "Risky",
					email: cleanEmail,
				}),
				signal: controller.signal,
			});

			if (!response.ok) {
				throw new Error("Subscription failed. Please try again.");
			}

			setSubscribeMessage("Thank you for subscribing! Check your inbox.");
			setEmail("");
			setCapVal(null);
			setShowRecaptcha(false);
		} catch (error) {
			setSubscribeMessage(
				error?.message || "Subscription failed. Please try again."
			);
		} finally {
			window.clearTimeout(timer);
		}
	}, [capVal, email, showRecaptcha]);

	return (
		<>
			{subscribeMessage && (
				<div
					className={`fixed bottom-4 right-4 text-white p-3 rounded-lg shadow-xl z-[1202] flex items-center gap-2 ${
						subscribeMessage.toLowerCase().includes("failed")
							? "bg-red-700"
							: "bg-green-700"
					}`}
					role="status"
					aria-live="polite"
				>
					<MailIcon />
					{subscribeMessage}
				</div>
			)}

			<footer
				ref={footerRef}
				className="w-full bg-[#713580] overflow-x-hidden text-white below-fold-content"
			>
				<div className="w-full md:px-10 px-6 lg:px-16 grid lg:grid-cols-4 md:grid-cols-2 gap-5 border-b border-white/40">
					<div className="flex justify-center items-start flex-col lg:px-10 md:px-4 md:py-2 lg:py-6">
						<h2 className="text-3xl font-lora my-4">Let's get in Touch</h2>

						<p className="font-poppins text-[14px]">
							Sign up for our newsletter and get news about latest products and discounts.
						</p>

						<label htmlFor="risky-footer-email" className="sr-only">
							Email address
						</label>

						<input
							id="risky-footer-email"
							name="email"
							type="email"
							autoComplete="email"
							value={email}
							onChange={(event) => setEmail(event.target.value)}
							aria-invalid={Boolean(emailError)}
							aria-describedby={
								emailError ? "risky-footer-email-error" : undefined
							}
							placeholder="Enter Your Email Address..."
							className="text-white placeholder:text-white font-poppins px-3 w-full outline-none border-2 bg-transparent text-[16px] py-2 rounded-lg my-2 border-white focus:ring-2 focus:ring-white"
						/>

						{emailError && (
							<p
								id="risky-footer-email-error"
								role="alert"
								className="text-white text-sm bg-red-800 p-1 rounded mt-1"
							>
								{emailError}
							</p>
						)}

						{showRecaptcha && (
							<Suspense
								fallback={
									<p className="text-sm" role="status">
										Loading verification…
									</p>
								}
							>
								<div className="mt-2">
									<ReCAPTCHA
										sitekey="6LcikiksAAAAAD6Pjibn5bvNJyYBPj1EMym0ryw7"
										onChange={setCapVal}
									/>
								</div>
							</Suspense>
						)}

						<button
							type="button"
							onClick={handleSubscribe}
							className="mt-4 bg-white text-[#5f2b70] font-semibold py-2 px-4 rounded hover:bg-gray-100 transition w-full"
						>
							{showRecaptcha ? "Complete Subscription" : "Subscribe"}
						</button>
					</div>

					<div className="font-Jost-Regular md:px-5 lg:px-5 flex flex-col gap-2 py-6 md:py-10 lg:py-14">
						<h2 className="font-lora mb-2 font-medium text-[20px]">
							Collections
						</h2>

						{entries.map(([category, value]) => {
							const key = category.replace(/\s+/g, "").toLowerCase();
							const open = openMenu === key;
							const years = value?.years || [];

							return (
								<div key={category} className="font-poppins">
									<button
										type="button"
										onClick={() => toggleMenu(key)}
										aria-expanded={open}
										aria-controls={`footer-${key}`}
										className="flex justify-between items-center w-full py-1 px-2 rounded hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
									>
										<span>{category}</span>
										<Chevron open={open} />
									</button>

									<div
										id={`footer-${key}`}
										className={`ml-4 mt-2 flex flex-col gap-1 overflow-hidden transition-all duration-300 ${
											open ? "max-h-72 opacity-100" : "max-h-0 opacity-0"
										}`}
									>
										{years.map((year) => (
											<Link
												key={year}
												to={getRoute(category, year)}
												aria-label={`${year} ${category} Collection`}
												className="py-1 px-2 rounded hover:bg-white/10"
											>
												{year} Collection
											</Link>
										))}

										<Link
											to={`/size-chart/${
												category.toLowerCase().includes("gown")
													? "gowns"
													: category.toLowerCase()
											}`}
											aria-label={`${category} Size Chart`}
											className="py-1 px-2 rounded hover:bg-white/10"
										>
											Size Chart
										</Link>
									</div>
								</div>
							);
						})}
					</div>

					<div className="font-Jost-Regular md:px-5 lg:px-5 flex flex-col gap-2 py-6 md:py-10 lg:py-14">
						<h2 className="font-lora mb-2 font-medium text-[20px]">
							Contact Links
						</h2>

						<Link
							to="/about"
							className="font-poppins block py-1 px-2 rounded hover:bg-white/10"
						>
							About Us
						</Link>

						<Link
							to="/contact"
							className="font-poppins block py-1 px-2 rounded hover:bg-white/10"
						>
							Contact Us
						</Link>

						<a
							href="https://market.riskydress.com/"
							target="_blank"
							rel="noopener noreferrer"
							className="mt-3 border border-white rounded-md w-fit px-4 py-2 font-semibold hover:bg-white/10"
						>
							Retailer Login
						</a>
					</div>

					<div className="md:px-5 lg:px-5 flex flex-col gap-2 py-6 md:py-10 lg:py-14">
						<h2 className="font-lora mb-2 font-medium text-[20px]">Contact</h2>

						<a
							href="mailto:Info@RiskyDress.com"
							className="font-poppins py-1 px-2 rounded hover:bg-white/10"
						>
							Info@RiskyDress.com
						</a>

						<a
							href="tel:+14508187078"
							className="font-poppins py-1 px-2 rounded hover:bg-white/10"
						>
							450-818-7078
						</a>
					</div>
				</div>
			</footer>
		</>
	);
}

export default memo(Footer);
